import { ColumnDef } from "@tanstack/react-table";
import { AxiosError } from "axios";
import React, { useEffect, useState, useRef } from "react";
import DataTable2 from "shared/DataTable2";
import UnifiedSearchBox from "../../shared/UnifiedSearchBox";
import ServerDeletedPills from "shared/ServerDeletedPills";
import { ServerDeletedFilterValue } from "shared/ServerListFilters";
import { useServerPaginatedList } from "../../shared/useServerPaginatedList";
import { rowMatchesUnifiedSearch } from "../../shared/unifiedSearch";
import { Button } from "../../components/ui/button";
import { LoginAxiosInstance } from "../../Network/LoginAxiosInstance";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/Label";
import { Toaster } from "../../components/ui/toaster";
import { useToast } from "../../hooks/use-toast";
import { ActivityCustom, Result } from "../../interface/ActivityCustom";
import { Option } from "../../interface/Option";
import { ActivityCustomAxiosInstance } from "../../Network/ActivityCustom";
import ListofActivityCode from "../Shared/ListOfActivityCode";
import ActivitySelector from "../Shared/ActivityType";
import ListOfActivityMedical from "../Shared/ListOfActivityMedical";
import ListOfActivityCustom from "../Shared/ListOfActivityCustom";
import FileUpload from "../uploadFile/UploadFileActivityCustomGroup/UploadFileActivityCustomGroup";

// ─── Role Helper ──────────────────────────────────────────────────────────────
const SectionCard: React.FC<{ num: number; title: string; subtitle?: string; children: React.ReactNode }> = ({ num, title, subtitle, children }) => (
  <div className="border border-gray-200 rounded-xl overflow-hidden mb-4 bg-white shadow-sm">
    <div className="flex items-center gap-3 px-5 py-3 bg-gray-50 border-b border-gray-200">
      <span className="w-6 h-6 rounded-full bg-blue-700 text-white text-xs font-semibold flex items-center justify-center flex-shrink-0">{num}</span>
      <div>
        <p className="text-sm font-semibold text-gray-800">{title}</p>
        {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
      </div>
    </div>
    <div className="p-5">{children}</div>
  </div>
);
const getUserRole = (): string => {
  try {
    const token = localStorage.getItem("token") || "";
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.userType || "";
  } catch { return ""; }
};

const ActivityCustomGroup: React.FC = () => {
  // Main data state
  const [items, setItems] = useState<ActivityCustom>({ result: [] });
  const [isEditLoading, setIsEditLoading] = useState(false);

  // ─── Role-based access ────────────────────────────────────────────────────
  const userRole = getUserRole();
  const canManageDeleted = userRole === "system_admin" || userRole === "super_super_admin";

  // ─── Force Dialog state ───────────────────────────────────────────────────
  const [forceDialogOpen, setForceDialogOpen] = useState(false);
  const [forceAction, setForceAction] = useState<"softDelete" | "hardDelete" | "status" | null>(null);
  const [forceId, setForceId] = useState<string | number | null>(null);
  const [forceError, setForceError] = useState<string>("");

  // Form states
  const [Name, setName] = useState<string>("");
  const [Description, setDescription] = useState<string>("");
  const [activitiesByType, setActivitiesByType] = useState<{
    CPT: Option[];
    HCPCS: Option[];
    Dental: Option[];
    Drugs: Option[];
  }>({ CPT: [], HCPCS: [], Dental: [], Drugs: [] });
  const [selectedActivities, setSelectedActivities] = useState<Option[]>([]);
  const [ActivityType, setActivityType] = useState<string>("");
  const [activitymedicalGroups, setActivityMedicalGroups] = useState<Option[]>([]);
  const [activitycustomGroups, setActivityCustomGroups] = useState<Option[]>([]);
  const [isActive, setIsActive] = useState<boolean>(true);

  // UI control states
  const [clearactivitycustomGroups, setClearActivityCustomGroups] = useState(false);
  const [clearListOfActivities, setClearListOfActivities] = useState(false);
  const [clearactivitymedicalGroups, setClearActivityMedicalGroups] = useState(false);
  const [activityTypeisValid, setActivityTypeIsValid] = useState<boolean>(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<null | number>(null);

  // dialog for details
  const [selectedItem, setSelectedItem] = useState<Result | null>(null);
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const [viewCodes, setViewCodes] = useState<string[]>([]);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);

  const [usersMap, setUsersMap] = useState<Record<number, string>>({});
  const [selectedActivitiesList, setSelectedActivitiesList] = useState<string[]>([]);
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const token = localStorage.getItem("token") || "";
  const [showAllUpdates, setShowAllUpdates] = useState(false);
  const { toast } = useToast();

  useEffect(() => { fetchUsers(); }, []);

  // ─── Dialog handlers ──────────────────────────────────────────────────────
  const handleAddClick = () => { setEditIndex(null); clear(); setIsDialogOpen(true); };
  const handleEditClick = (item: Result) => { handleEdit(item); setIsDialogOpen(true); };
  const handleOpenDialog = (item: Result) => { setSelectedItem(item); dialogRef.current?.showModal(); };
const handleCloseDialog = () => { setSelectedItem(null); setShowAllUpdates(false); };
  const handleOpenActivityDialog = (activities: string[]) => { setSelectedActivitiesList(activities); setActivityDialogOpen(true); };
  const handleCloseActivityDialog = () => { setActivityDialogOpen(false); setSelectedActivitiesList([]); };

  // ─── Advanced search ──────────────────────────────────────────────────────
  const [filterSearch, setFilterSearch] = useState<string>("");
  const [filterStatus, setFilterStatus] = useState<string>("");
  const [deletedFilter, setDeletedFilter] = useState<ServerDeletedFilterValue>("false");


  // ─── Form handlers ────────────────────────────────────────────────────────
  const handleActivityTypeChange = (newActivityType: string) => {
    if (ActivityType && selectedActivities.length > 0) {
      setActivitiesByType(prev => ({ ...prev, [ActivityType]: selectedActivities }));
    }
    setActivityType(newActivityType);
    const newTypeActivities = activitiesByType[newActivityType as keyof typeof activitiesByType] || [];
    setSelectedActivities(newTypeActivities);
    setClearListOfActivities(true);
    setTimeout(() => { setClearListOfActivities(false); }, 100);
  };

  const handleActivitiesChange = (selectedOptions: Option[]) => {
    setSelectedActivities(selectedOptions);
    if (ActivityType) {
      setActivitiesByType(prev => ({ ...prev, [ActivityType]: selectedOptions }));
    }
  };

  const handleactivitymedicalChange = (selectedOptions: Option[]) => setActivityMedicalGroups(selectedOptions);
  const handleactivitycustomChange = (selectedOptions: Option[]) => setActivityCustomGroups(selectedOptions);

  const clear = () => {
    setName(""); setDescription(""); setSelectedActivities([]); setActivityType("");
    setActivitiesByType({ CPT: [], HCPCS: [], Dental: [], Drugs: [] });
    setActivityMedicalGroups([]); setActivityCustomGroups([]); setIsActive(true);
  };

  // ─── Fetch ────────────────────────────────────────────────────────────────

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalCount,
    allowedPageSizes,
    isTableLoading,
    manualPagination,
    fetchItems,
  } = useServerPaginatedList<Result, { search: string; status: string; deleted: ServerDeletedFilterValue }>({
    filters: { search: filterSearch, status: filterStatus, deleted: deletedFilter },
    fetchPage: ({ page, limit, filters }) =>
      ActivityCustomAxiosInstance.get(
        "getActivityCustomGroup",
        {
          params: {
            page,
            limit,
            is_active: filters.status === "true" || filters.status === "false" ? filters.status : undefined,
            is_deleted: filters.deleted,
          },
        }
      ),
    setRows: (rows) => setItems({ result: rows }),
    sortRows: (rows) =>
      [...rows]
        .sort((a: any, b: any) => Number(b.id) - Number(a.id))
        .sort((a: any, b: any) =>
          a.is_deleted === b.is_deleted ? 0 : a.is_deleted ? 1 : -1
        ),
    isClientFallbackActive: (filters) => filters.search.trim().length >= 2,
    clientFilterRows: (rows, filters) =>
      rows.filter((item) => rowMatchesUnifiedSearch(item, filters.search)),
    onError: (error) => {
      console.error("Failed to fetch Activity Custom Groups", error);
      toast({
        title: "Error",
        description: "Failed to fetch Activity Custom Groups",
        variant: "destructive",
      });
    },
  });

  const fetchUsers = async () => {
    try {
      const res = await LoginAxiosInstance.get("/");
      const raw: any[] = res.data?.users ?? res.data?.user ?? res.data ?? [];
      const map: Record<number, string> = {};
      raw.forEach((u: any) => { if (u.id != null) map[Number(u.id)] = u.name ?? u.username ?? String(u.id); });
      setUsersMap(map);
    } catch {}
  };

  const resolveUserName = (val: any): string => {
    if (val == null) return "—";
    if (typeof val === "object") return val.name ?? val.username ?? "—";
    if (typeof val === "string" && isNaN(Number(val))) return val;
    return usersMap[Number(val)] ?? String(val);
  };

  // ─── Toggle Active ────────────────────────────────────────────────────────
  const toggleActiveStatus = async (item: Result, force = false): Promise<void> => {
    try {
      const allActivitiesWithTypes: any[] = [];
      if (item.activity_types && Array.isArray(item.activity_types)) {
        item.activity_types.forEach((at: any) => { allActivitiesWithTypes.push({ code: at.code, type: at.type }); });
      } else if (item.activity_code && Array.isArray(item.activity_code)) {
        item.activity_code.forEach((code: any, idx: number) => {
          let type = "CPT";
          if (item.types && Array.isArray(item.types) && item.types[idx]) {
            const typeMap: Record<number, string> = { 1: "CPT", 2: "Dental", 3: "HCPCS", 4: "Drugs" };
            type = typeMap[item.types[idx]] || "CPT";
          }
          allActivitiesWithTypes.push({ code, type });
        });
      }
      const ValuesActivityMedical = (item.medical_groups || []).map((g: any) => g.id ?? g);
      const ValuesActivityCustom = (item.custom_groups || []).map((g: any) => g.id ?? g);
      const updatedItem = {
        data: {
          name: item.name, activity_type: item.activity_type || "",
          activity_code: allActivitiesWithTypes.map((a) => a.code),
          activity_types: allActivitiesWithTypes, description: item.description,
          medical_groups: ValuesActivityMedical, custom_groups: ValuesActivityCustom,
          is_active: !item.is_active, id: item.id,
        },
        token,
      };
      const url = force ? "updateActivityCustomGroup?force=true" : "updateActivityCustomGroup";
      const response = await ActivityCustomAxiosInstance.put(url, updatedItem);
      if (response.data.success) {
        setItems((prev) => ({ result: prev.result.map((i) => i.id === item.id ? { ...i, is_active: !item.is_active } : i) }));
        setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null);
        toast({ title: "Success", description: `Status changed to ${!item.is_active ? "Active" : "Inactive"}.` });
      } else {
        setForceId(item.id); setForceAction("status");
        setForceError(response.data.error || "This record is used elsewhere.");
        setForceDialogOpen(true);
      }
    } catch (error: any) {
      const errMsg = error.response?.data?.error || "";
      if (error.response?.status === 409 || errMsg.toLowerCase().includes("used") || errMsg.toLowerCase().includes("referenced") || errMsg.toLowerCase().includes("cannot")) {
        setForceId(item.id); setForceAction("status");
        setForceError(errMsg || "This record is used elsewhere."); setForceDialogOpen(true);
      } else {
        toast({ title: "Error", description: errMsg || "An unexpected error occurred.", variant: "destructive" });
      }
    }
  };

  // ─── Soft Delete ──────────────────────────────────────────────────────────
  const removeItem = async (id: string | number): Promise<void> => {
    try {
      const response = await ActivityCustomAxiosInstance.delete(
        `softDeleteActivityCustomGroup/${id}`, { data: { token } }
      );
      if (response.data.success) {
        await fetchItems();
        toast({ title: "Success", description: "Activity Custom Group deleted successfully." });
      } else {
        setForceId(id); setForceAction("softDelete");
        setForceError(response.data.error || "This record is used elsewhere.");
        setForceDialogOpen(true);
      }
    } catch (error: any) {
      const errMsg = error.response?.data?.error || "Failed to delete";
      if (error.response?.status === 409 || errMsg.toLowerCase().includes("used") || errMsg.toLowerCase().includes("referenced") || errMsg.toLowerCase().includes("cannot")) {
        setForceId(id); setForceAction("softDelete"); setForceError(errMsg); setForceDialogOpen(true);
      } else {
        toast({ title: "Error", description: errMsg, variant: "destructive" });
      }
    }
  };

  // ─── Restore ──────────────────────────────────────────────────────────────
  const restoreItem = async (id: string | number): Promise<void> => {
    try {
      const response = await ActivityCustomAxiosInstance.patch(
        `restoreActivityCustomGroup/${id}`, { token }
      );
      if (response.data.success) {
        await fetchItems();
        toast({ title: "Success", description: "Activity Custom Group restored successfully." });
      } else {
        toast({ title: "Error", description: response.data.error || "Failed to restore", variant: "destructive" });
      }
    } catch (error: any) {
      toast({ title: "Error", description: error.response?.data?.error || "Failed to restore", variant: "destructive" });
    }
  };

  // ─── Hard Delete (permanent) ──────────────────────────────────────────────
  const permanentDeleteItem = async (id: string | number, force = false): Promise<void> => {
    try {
      const url = force ? `deleteActivityCustomGroup/${id}?force=true` : `deleteActivityCustomGroup/${id}`;
      const response = await ActivityCustomAxiosInstance.delete(url, { data: { token } });
      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null);
        toast({ title: "Success", description: "Activity Custom Group permanently deleted." });
      } else {
        setForceAction("hardDelete");
        setForceError(response.data.error || "This record is used elsewhere.");
      }
    } catch (error: any) {
      setForceAction("hardDelete");
      setForceError(error.response?.data?.error || "Failed to delete");
    }
  };

  // ─── Force Soft Delete ────────────────────────────────────────────────────
  const forceSoftDelete = async (id: string | number): Promise<void> => {
    try {
      const response = await ActivityCustomAxiosInstance.delete(
        `softDeleteActivityCustomGroup/${id}?force=true`, { data: { token } }
      );
      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null);
        toast({ title: "Success", description: "Activity Custom Group force deleted successfully." });
      } else {
        setForceError(response.data.error || "Failed to force delete");
      }
    } catch (error: any) {
      setForceError(error.response?.data?.error || "Failed to force delete");
    }
  };

  // ─── Add ──────────────────────────────────────────────────────────────────
  const addItem = async (): Promise<void> => {
    if (!Name.trim()) { toast({ title: "Error", description: "Name is required and cannot be empty.", variant: "destructive" }); return; }
    if (Name.trim().length < 3) { toast({ title: "Error", description: "Name must be at least 3 characters long.", variant: "destructive" }); return; }
    const hasAnyActivities = Object.values(activitiesByType).some(activities => activities.length > 0);
    if (!hasAnyActivities) { toast({ title: "Error", description: "At least one Activity code is required for any type.", variant: "destructive" }); return; }

    const ValuesActivityMedical = activitymedicalGroups.map((option) => option.value);
    const ValuesActivityCustom = activitycustomGroups.map((option) => option.value);
    const allActivitiesWithTypes: any[] = [];
    Object.entries(activitiesByType).forEach(([type, activities]) => {
      if (activities && activities.length > 0) {
        activities.forEach(activity => { allActivitiesWithTypes.push({ code: activity.value, type: type }); });
      }
    });

    try {
      const newGroupArray = [{
        name: Name, activity_type: ActivityType,
        activity_code: allActivitiesWithTypes.map(a => a.code),
        activity_types: allActivitiesWithTypes, description: Description,
        medical_groups: ValuesActivityMedical, custom_groups: ValuesActivityCustom, is_active: isActive,
      }];
      const payload = { data: newGroupArray, token };
      const response = await ActivityCustomAxiosInstance.post("addNewActivityCustomGroup", payload);
      if (response.data.success) {
        await fetchItems(); setCurrentPage(0); clear(); setIsDialogOpen(false);
        toast({ title: "Success", description: "New Activity Custom Group added successfully." });
      } else {
        const errors = response.data.errors || [];
        const errorMessages = errors.map((error: any) => error.error).join("\n");
        toast({ title: "Error", description: errorMessages || response.data.message || "Failed to add the group.", variant: "destructive" });
      }
    } catch (error: AxiosError | any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };

  // ─── Edit ─────────────────────────────────────────────────────────────────
  const handleEdit = (item: Result) => {
    setIsEditLoading(true);
    setName(item.name);
    setActivitiesByType({ CPT: [], HCPCS: [], Dental: [], Drugs: [] });

    if (item.activity_types && Array.isArray(item.activity_types) && item.activity_types.length > 0) {
      const grouped = { CPT: [] as Option[], HCPCS: [] as Option[], Dental: [] as Option[], Drugs: [] as Option[] };
      item.activity_types.forEach((actItem: any) => {
        if (actItem.type && actItem.code) {
          const option: Option = { value: actItem.code, label: actItem.code.toString() };
          if (grouped[actItem.type as keyof typeof grouped]) grouped[actItem.type as keyof typeof grouped].push(option);
        }
      });
      setActivitiesByType(grouped);
    } else if (item.activity_code && Array.isArray(item.activity_code) && item.activity_code.length > 0) {
      const grouped = { CPT: [] as Option[], HCPCS: [] as Option[], Dental: [] as Option[], Drugs: [] as Option[] };
      item.activity_code.forEach((code: any, idx: number) => {
        let type = 'CPT';
        if (item.types && Array.isArray(item.types) && item.types[idx]) {
          const typeMap: Record<number, string> = { 1: "CPT", 2: "Dental", 3: "HCPCS", 4: "Drugs" };
          type = typeMap[item.types[idx]] || 'CPT';
        } else if (item.activity_type) {
          const validActivityTypes: Record<string, string> = { cpt: "CPT", dental: "Dental", hcpcs: "HCPCS", drugs: "Drugs" };
          type = validActivityTypes[item.activity_type.toLowerCase()] || item.activity_type;
        }
        const option: Option = { value: code, label: code.toString() };
        if (grouped[type as keyof typeof grouped]) grouped[type as keyof typeof grouped].push(option);
      });
      setActivitiesByType(grouped);
    }

    const MedicalActivityGroupsData = Array.isArray(item.medical_groups)
      ? item.medical_groups.map((obj) => typeof obj === "string" ? { id: 0, name: obj } : obj) : [];
    setActivityMedicalGroups(MedicalActivityGroupsData.map((obj) => ({ value: obj.id, label: obj.name })));

    const CustomActivityGroupsData = Array.isArray(item.custom_groups)
      ? item.custom_groups.map((obj) => typeof obj === "string" ? { id: 0, name: obj } : obj) : [];
    setActivityCustomGroups(CustomActivityGroupsData.map((obj) => ({ value: obj.id, label: obj.name })));

    setIsActive(item.is_active);
    setDescription(item.description);
    setEditIndex(item.id);
    setTimeout(() => { setIsEditLoading(false); }, 100);
  };

  // ─── Update ───────────────────────────────────────────────────────────────
  const updateItem = async (id: string | number): Promise<void> => {
    if (!Name.trim()) { toast({ title: "Error", description: "Name is required and cannot be empty.", variant: "destructive" }); return; }
    if (Name.trim().length < 3) { toast({ title: "Error", description: "Name must be at least 3 characters long.", variant: "destructive" }); return; }
    const hasAnyActivities = Object.values(activitiesByType).some(activities => activities.length > 0);
    if (!hasAnyActivities) { toast({ title: "Error", description: "At least one Activity code is required for any type.", variant: "destructive" }); return; }

    try {
      const ValuesActivityMedical = activitymedicalGroups.map((option) => option.value);
      const ValuesActivityCustom = activitycustomGroups.map((option) => option.value);
      const allActivitiesWithTypes: any[] = [];
      Object.entries(activitiesByType).forEach(([type, activities]) => {
        if (activities && activities.length > 0) {
          activities.forEach(activity => { allActivitiesWithTypes.push({ code: activity.value, type: type }); });
        }
      });
      const updatedItem = {
        data: {
          name: Name, activity_type: ActivityType,
          activity_code: allActivitiesWithTypes.map(a => a.code),
          activity_types: allActivitiesWithTypes, description: Description,
          medical_groups: ValuesActivityMedical, custom_groups: ValuesActivityCustom,
          is_active: isActive, id: id,
        },
        token: token,
      };
      const response = await ActivityCustomAxiosInstance.put(`updateActivityCustomGroup`, updatedItem);
      if (response.data.success) {
        fetchItems(); clear(); setIsDialogOpen(false);
        toast({ title: "Success", description: "Activity Custom group updated successfully." });
      }
    } catch (error: AxiosError | any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };

  const handleDownload = (item: Result) => {
    const rows = ["name;description;activity_type;activity_code;medical_groups;custom_groups;types;is_active"];
    const ActivityCodes = item.activity_code || [];
    const medicalGroups = item.medical_groups || [];
    const customGroups = item.custom_groups || [];
    const maxLen = Math.max(ActivityCodes.length, medicalGroups.length, customGroups.length);
    if (maxLen > 0) {
      const Activity = ActivityCodes.length > 0 ? ActivityCodes[0] : "";
      const medGroupName = medicalGroups.length > 0 ? medicalGroups[0].name || "" : "";
      const custGroupName = customGroups.length > 0 ? customGroups[0].name || "" : "";
      rows.push(`${item.name || ""};${item.description || ""};${item.activity_type || ""};${Activity};${medGroupName};${custGroupName};${item.types || ""};${item.is_active ? "TRUE" : "FALSE"}`);
      for (let index = 1; index < maxLen; index++) {
        const Activity = index < ActivityCodes.length ? ActivityCodes[index] : "";
        const medGroupName = index < medicalGroups.length ? medicalGroups[index].name || "" : "";
        const custGroupName = index < customGroups.length ? customGroups[index].name || "" : "";
        rows.push(`${item.name || ""};;${Activity};${medGroupName};${custGroupName};`);
      }
    } else {
      rows.push(`${item.name || ""};${item.description || ""};;;;${item.types || ""};${item.is_active ? "TRUE" : "FALSE"}`);
    }
    const csvContent = rows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `custom_activity_group_${item.id}.csv`; a.click();
    window.URL.revokeObjectURL(url);
  };

  const getActivityTypeColor = (type: string) => {
    const colors: Record<string, { bg: string; border: string; text: string }> = {
      'CPT':    { bg: 'bg-blue-100',   border: 'border-blue-500',   text: 'text-blue-900' },
      'HCPCS':  { bg: 'bg-green-100',  border: 'border-green-500',  text: 'text-green-900' },
      'Dental': { bg: 'bg-purple-100', border: 'border-purple-500', text: 'text-purple-900' },
      'Drugs':  { bg: 'bg-orange-100', border: 'border-orange-500', text: 'text-orange-900' },
    };
    return colors[type] || { bg: 'bg-gray-100', border: 'border-gray-500', text: 'text-gray-900' };
  };

  // ─── Columns ──────────────────────────────────────────────────────────────
  const columns: ColumnDef<Result>[] = [
    {
      accessorKey: "id",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          ID {column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
    cell: ({ row }) => (
  <div className="text-center w-full font-medium">
    {row.index + 1}
  </div>
),
    },
    {
      accessorKey: "name",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Name {column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const isDeleted = (row.original as any).is_deleted;
        return (
          <div className={`w-full text-center ${isDeleted ? "line-through text-gray-400" : ""}`}>
            {row.getValue("name")}
          </div>
        );
      },
    },
    {
      accessorKey: "description",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Description {column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const description = row.getValue("description") as string;
        return (
          <div className="w-full text-center px-2">
            {description && description.length > 50
              ? <span title={description}>{description.substring(0, 50)}...</span>
              : <span>{description || "-"}</span>}
          </div>
        );
      },
    },
    {
      accessorKey: "activity_code",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Activity Codes {column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        let allCodesWithTypes: Array<{ code: string | number; type: string }> = [];
        if (item.activity_types && Array.isArray(item.activity_types)) {
          allCodesWithTypes = item.activity_types.map((at: any) => ({ code: at.code, type: at.type }));
        } else if (item.activity_code && Array.isArray(item.activity_code)) {
          allCodesWithTypes = item.activity_code.map((code: any, idx: number) => {
            let type = 'Unknown';
            if (item.types && Array.isArray(item.types) && item.types[idx]) {
              const typeMap: Record<number, string> = { 1: "CPT", 2: "Dental", 3: "HCPCS", 4: "Drugs" };
              type = typeMap[item.types[idx]] || 'Unknown';
            }
            return { code, type };
          });
        }
        if (allCodesWithTypes.length === 0) return <div className="w-full text-center text-gray-400">-</div>;

        const searchTerm = filterSearch.toLowerCase().trim();
        const hasSearch = searchTerm.length >= 2;
        const filteredCodes = hasSearch
          ? allCodesWithTypes.filter(item => item.code.toString().toLowerCase().includes(searchTerm))
          : allCodesWithTypes;
        const medicalItems = Array.isArray(item.medical_groups)
          ? item.medical_groups.map((g: any) => ({ code: g.name || g, type: "Medical Group" })) : [];
        const customItems = Array.isArray(item.custom_groups)
          ? item.custom_groups.map((g: any) => ({ code: g.name || g, type: "Custom Group" })) : [];
        const allItems = [...filteredCodes, ...medicalItems, ...customItems];
        const displayCodes = allItems.slice(0, 3);
        const hasMoreCodes = allItems.length > 3;

        return (
          <div className="w-full px-3 py-2">
            <div className="flex flex-wrap justify-center gap-2 mb-2">
              {displayCodes.map((c, idx) => {
                const isMatching = hasSearch && c.code.toString().toLowerCase().includes(searchTerm);
                const colors = getActivityTypeColor(c.type);
                return (
                  <div key={idx} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all ${isMatching ? 'bg-yellow-50 border-yellow-400' : `${colors.bg} ${colors.border}`}`}>
                    <span className={`font-semibold text-sm ${isMatching ? 'text-yellow-900' : colors.text}`}>{c.code}</span>
                    <span className={`text-[10px] font-bold capitalize ${isMatching ? 'text-yellow-700' : colors.text}`}>{c.type}</span>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-gray-200">
              <span className="text-xs text-gray-600">
                {hasSearch
                  ? <><span className="font-bold text-yellow-600">{allItems.length}</span><span className="mx-1">/</span><span className="font-bold">{allCodesWithTypes.length + medicalItems.length + customItems.length}</span></>
                  : <span className="font-bold">{allItems.length}</span>}
              </span>
              {hasMoreCodes && (
                <button
                  onClick={() => {
                    const allCodes = [
                      ...allCodesWithTypes.map(c => `${c.code}|||${c.type}`),
                      ...Array.isArray(item.medical_groups) ? item.medical_groups.map((g: any) => `${g.name || g}|||Medical Group`) : [],
                      ...Array.isArray(item.custom_groups) ? item.custom_groups.map((g: any) => `${g.name || g}|||Custom Group`) : [],
                    ];
                    handleOpenActivityDialog(allCodes);
                  }}
                  className="text-xs text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-1">
                  <span>All codes </span>
                  <i className="fa-solid fa-plus text-[10px]"></i>
                </button>
              )}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "is_active",
      enableSorting: false,
      header: () => <div className="w-full text-center normal-case font-normal">Status</div>,
      cell: ({ row }) => {
        const item = row.original;
        const isDeleted = (item as any).is_deleted;
        return (
          <div className="w-full flex justify-center">
            <button
              onClick={() => !isDeleted && toggleActiveStatus(item)}
              disabled={!!isDeleted}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isDeleted ? "opacity-40 cursor-not-allowed bg-gray-200" : item.is_active ? "bg-green-500" : "bg-gray-300"}`}>
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${item.is_active && !isDeleted ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </div>
        );
      },
    },
    {
      id: "view",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center">View</Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="w-full flex justify-center py-2">
            <button onClick={() => handleOpenDialog(item)} className="text-blue-600 hover:text-blue-800" title="View Details">
              <i className="fa-solid fa-eye text-lg"></i>
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="h5-container mx-auto px-1 pt-16 pb-4">
      <div className="flex justify-between items-center">
        <h5 className="mb-6 flex items-center gap-2 font-bold text-2xl mt-4 text-blue-900">
          <span className="icon-container flex items-center justify-center w-10 h-10 rounded-full bg-blue-900 text-white">
            <i className="fa-solid fa-folder-plus"></i>
          </span>
          Activity Custom Group
        </h5>
      </div>

      <DataTable2
        columns={columns}
        data={items.result}
        addButtonLabel="Add New Group"
        showAddButton={false}
        onEdit={handleEdit}
        onDelete={(id) => {
          const item = items.result.find(i => i.id === id);
          if ((item as any)?.is_deleted) {
            setForceId(id); setForceAction("hardDelete"); setForceError(""); setForceDialogOpen(true);
          } else {
            removeItem(id);
          }
        }}
        onRestore={canManageDeleted ? restoreItem : undefined}
canHardDelete={canManageDeleted}
        pageIndex={currentPage}
        onPageChange={setCurrentPage}
        manualPagination={manualPagination}
        pageCount={totalPages}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageSizeChange={(nextPageSize) => {
          setPageSize(nextPageSize);
          setCurrentPage(0);
        }}
        allowedPageSizes={allowedPageSizes}
        isLoading={isTableLoading}
        onAdd={handleAddClick}
        onEditClick={handleEditClick}
        onDownload={handleDownload}
        isRowDeleted={(row: any) => row.is_deleted === true}
        toolbarExtras={
          <div style={{ width: "100%", position: "relative" }}>
<div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "10px 160px 10px 16px", background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)", borderRadius: 12, border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.04)", marginBottom: 12 }}>
  <ServerDeletedPills
    value={deletedFilter}
    onChange={(value) => { setDeletedFilter(value); setCurrentPage(0); }}
    />
              <button type="button" onClick={handleAddClick}
                style={{ display: "flex", alignItems: "center", gap: 7, padding: "8px 18px", fontSize: 13, fontWeight: 600, color: "#ffffff", background: "#1e3a8a", border: "none", borderRadius: 8, cursor: "pointer", whiteSpace: "nowrap", boxShadow: "0 2px 6px rgba(30,58,138,0.25)" }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14"/></svg>
                Add New Group
              </button>
              <div style={{ width: 1, height: 28, background: "#cbd5e1" }} />
              <UnifiedSearchBox
                value={filterSearch}
                onChange={(value) => { setFilterSearch(value); setCurrentPage(0); }}
                helpText="Group Name, Activity Code, Medical Group, or Custom Group."
              />
              <div style={{ width: 1, height: 28, background: "#cbd5e1" }} />
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b", letterSpacing: "0.05em" }}>Status</span>
                <div style={{ display: "flex", alignItems: "center", padding: 4, background: "#e2e8f0", borderRadius: 9999 }}>
                  {[{ value: "", label: "All" }, { value: "true", label: "Active" }, { value: "false", label: "Inactive" }].map((option) => (
                    <button key={option.value} type="button" onClick={() => setFilterStatus(option.value)}
                      style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", fontSize: 13, fontWeight: 500, borderRadius: 9999, border: "none", cursor: "pointer",
                        background: filterStatus === option.value ? option.value === "true" ? "#10b981" : option.value === "false" ? "#94a3b8" : "#ffffff" : "transparent",
                        color: filterStatus === option.value ? option.value === "" ? "#334155" : "#ffffff" : "#64748b",
                        boxShadow: filterStatus === option.value ? "0 2px 4px rgba(0,0,0,0.1)" : "none" }}>
                      {option.value === "true" && filterStatus === "true" && <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#ffffff" }} />}
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ position: "absolute", top: 0, right: 16, height: 62, display: "flex", alignItems: "center" }}>
              <FileUpload fetchItems={fetchItems} />
            </div>
          </div>
        }
      />

      {/* ─── View Details Dialog ─── */}
      <Dialog open={Boolean(selectedItem)} onOpenChange={(open) => { if (!open) handleCloseDialog(); }}>
        <DialogContent className="max-w-[780px] max-h-[92vh] flex flex-col p-0 overflow-hidden bg-white border-none rounded-2xl"
          style={{ boxShadow: "0 25px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)" }}>
          {selectedItem && (
            <>
              {/* Header */}
              <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #2563eb 100%)", padding: "24px 28px 20px", flexShrink: 0, position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", top: -40, right: -40, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
                <div style={{ position: "absolute", bottom: -30, left: -20, width: 100, height: 100, borderRadius: "50%", background: "rgba(255,255,255,0.03)" }} />
                <div style={{ display: "flex", alignItems: "flex-start", gap: 16, position: "relative", zIndex: 1 }}>
                  <div style={{ width: 48, height: 48, borderRadius: 14, background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, color: "#fff", flexShrink: 0, border: "1px solid rgba(255,255,255,0.1)" }}>
                    <i className="fa-solid fa-layer-group" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 2 }}>
                      <h2 style={{ fontSize: 20, fontWeight: 700, color: "#fff", margin: 0, letterSpacing: "-0.01em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {selectedItem.name}
                      </h2>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 12px", borderRadius: 9999, fontSize: 11, fontWeight: 600, flexShrink: 0, background: selectedItem.is_active ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.15)", color: selectedItem.is_active ? "#86efac" : "#fca5a5", border: `1px solid ${selectedItem.is_active ? "rgba(34,197,94,0.25)" : "rgba(239,68,68,0.2)"}` }}>
                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} />
                        {selectedItem.is_active ? "Active" : "Inactive"}
                      </span>
                      {(selectedItem as any).is_deleted && (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 12px", borderRadius: 9999, fontSize: 11, fontWeight: 600, flexShrink: 0, background: "rgba(239,68,68,0.15)", color: "#fca5a5", border: "1px solid rgba(239,68,68,0.2)" }}>
                          <i className="fa-solid fa-trash" style={{ fontSize: 10 }} /> Deleted
                        </span>
                      )}
                    </div>
                    <p style={{ fontSize: 12, color: "rgba(147,197,253,0.8)", margin: 0, fontWeight: 400 }}>
                      Activity Custom Group — <span style={{ fontWeight: 600, color: "#93c5fd" }}>#{selectedItem.id}</span>
                    </p>
                  </div>
                </div>
                {(() => {
                  const groupedByType: Record<string, Array<string | number>> = { CPT: [], HCPCS: [], Dental: [], Drugs: [] };
                  if (selectedItem.activity_types && Array.isArray(selectedItem.activity_types)) {
                    selectedItem.activity_types.forEach((at: any) => { if (at.type && at.code && groupedByType[at.type]) groupedByType[at.type].push(at.code); });
                  } else if (selectedItem.activity_code && Array.isArray(selectedItem.activity_code)) {
                    selectedItem.activity_code.forEach((code: any, idx: number) => {
                      let type = "CPT";
                      if (selectedItem.types && Array.isArray(selectedItem.types) && selectedItem.types[idx]) {
                        const typeMap: Record<number, string> = { 1: "CPT", 2: "Dental", 3: "HCPCS", 4: "Drugs" };
                        type = typeMap[selectedItem.types[idx]] || "CPT";
                      }
                      if (groupedByType[type]) groupedByType[type].push(code);
                    });
                  }
                  const totalCodes = Object.values(groupedByType).flat().length;
                  return (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginTop: 18, position: "relative", zIndex: 1 }}>
                      {[
                        { icon: "fa-code",        label: "Activity Codes", value: `${totalCodes} codes`,                            accent: "#60a5fa" },
                        { icon: "fa-hospital",     label: "Medical Groups", value: `${(selectedItem.medical_groups || []).length}`, accent: "#a78bfa" },
                        { icon: "fa-puzzle-piece", label: "Custom Groups",  value: `${(selectedItem.custom_groups || []).length}`,  accent: "#34d399" },
                      ].map((card, i) => (
                        <div key={i} style={{ background: "rgba(255,255,255,0.07)", backdropFilter: "blur(6px)", borderRadius: 12, padding: "12px 14px", border: "1px solid rgba(255,255,255,0.08)" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                            <i className={`fa-solid ${card.icon}`} style={{ fontSize: 11, color: card.accent, opacity: 0.9 }} />
                            <span style={{ fontSize: 10, color: "rgba(147,197,253,0.7)", fontWeight: 500, letterSpacing: "0.03em" }}>{card.label}</span>
                          </div>
                          <span style={{ fontSize: 15, fontWeight: 700, color: card.accent }}>{card.value}</span>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>

              {/* Body */}
              <div style={{ overflowY: "auto", flex: 1, background: "#f8fafc", padding: "6px 0 16px" }}>
                {selectedItem.description && (
                  <>
                    <div style={{ padding: "20px 28px 16px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                        <div style={{ width: 28, height: 28, borderRadius: 8, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #bbf7d0" }}>
                          <i className="fa-solid fa-align-left" style={{ fontSize: 12, color: "#16a34a" }} />
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Description</span>
                      </div>
                      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                        <p style={{ margin: 0, fontSize: 14, color: "#334155", lineHeight: 1.6 }}>{selectedItem.description}</p>
                      </div>
                    </div>
                    <div style={{ height: 1, background: "#e2e8f0", margin: "0 28px" }} />
                  </>
                )}

                <div style={{ padding: "20px 28px 16px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #dbeafe" }}>
                      <i className="fa-solid fa-code" style={{ fontSize: 12, color: "#3b82f6" }} />
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Activity Codes by Type</span>
                  </div>
                  {(() => {
                    const groupedByType: Record<string, Array<string | number>> = { CPT: [], HCPCS: [], Dental: [], Drugs: [] };
                    if (selectedItem.activity_types && Array.isArray(selectedItem.activity_types)) {
                      selectedItem.activity_types.forEach((at: any) => { if (at.type && at.code && groupedByType[at.type]) groupedByType[at.type].push(at.code); });
                    } else if (selectedItem.activity_code && Array.isArray(selectedItem.activity_code)) {
                      selectedItem.activity_code.forEach((code: any, idx: number) => {
                        let type = "CPT";
                        if (selectedItem.types && Array.isArray(selectedItem.types) && selectedItem.types[idx]) {
                          const typeMap: Record<number, string> = { 1: "CPT", 2: "Dental", 3: "HCPCS", 4: "Drugs" };
                          type = typeMap[selectedItem.types[idx]] || "CPT";
                        }
                        if (groupedByType[type]) groupedByType[type].push(code);
                      });
                    }
                    const totalCodes = Object.values(groupedByType).flat().length;
                    const typeAccents: Record<string, { bg: string; color: string; border: string }> = {
                      CPT:    { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" },
                      HCPCS:  { bg: "#faf5ff", color: "#7e22ce", border: "#e9d5ff" },
                      Dental: { bg: "#f0fdf4", color: "#166534", border: "#bbf7d0" },
                      Drugs:  { bg: "#fff7ed", color: "#c2410c", border: "#fed7aa" },
                    };
                    return totalCodes === 0 ? (
                      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "24px", textAlign: "center", color: "#94a3b8", fontSize: 13, fontStyle: "italic" }}>
                        <i className="fa-solid fa-info-circle" style={{ marginRight: 6 }} />No activity codes available
                      </div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        {Object.entries(groupedByType).map(([type, codes]) => {
                          if (codes.length === 0) return null;
                          const accent = typeAccents[type] || typeAccents.CPT;
                          return (
                            <div key={type} style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                              <div style={{ padding: "10px 14px", display: "flex", alignItems: "center", gap: 8, background: accent.bg, borderBottom: `1px solid ${accent.border}` }}>
                                <i className="fa-solid fa-stethoscope" style={{ fontSize: 11, color: accent.color }} />
                                <span style={{ flex: 1, fontSize: 12, fontWeight: 700, color: accent.color }}>{type}</span>
                                <span style={{ fontSize: 11, padding: "2px 10px", borderRadius: 9999, background: "rgba(255,255,255,0.7)", color: accent.color, fontWeight: 600, border: `1px solid ${accent.border}` }}>
                                  {codes.length} code{codes.length !== 1 ? "s" : ""}
                                </span>
                              </div>
                              <div style={{ padding: "12px 14px", display: "flex", flexWrap: "wrap" as const, gap: 6 }}>
                                {codes.map((code, idx) => (
                                  <span key={idx} style={{ display: "inline-flex", alignItems: "center", padding: "3px 10px", borderRadius: 9999, fontSize: 11, fontWeight: 500, background: accent.bg, color: accent.color, border: `0.5px solid ${accent.border}` }}>{code}</span>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>

                {(selectedItem.medical_groups || []).length > 0 && (
                  <>
                    <div style={{ height: 1, background: "#e2e8f0", margin: "0 28px" }} />
                    <div style={{ padding: "20px 28px 16px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                        <div style={{ width: 28, height: 28, borderRadius: 8, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #bbf7d0" }}>
                          <i className="fa-solid fa-hospital" style={{ fontSize: 12, color: "#16a34a" }} />
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Medical Activity Groups</span>
                        <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 500 }}>{(selectedItem.medical_groups || []).length} items</span>
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 6 }}>
                        {(selectedItem.medical_groups || []).map((group: any, idx: number) => (
                          <span key={idx} style={{ display: "inline-flex", alignItems: "center", padding: "3px 10px", borderRadius: 9999, fontSize: 11, fontWeight: 500, background: "#f0fdf4", color: "#166534", border: "0.5px solid #bbf7d0" }}>{group.name || group}</span>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {(selectedItem.custom_groups || []).length > 0 && (
                  <>
                    <div style={{ height: 1, background: "#e2e8f0", margin: "0 28px" }} />
                    <div style={{ padding: "20px 28px 16px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                        <div style={{ width: 28, height: 28, borderRadius: 8, background: "#fffbeb", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #fde68a" }}>
                          <i className="fa-solid fa-puzzle-piece" style={{ fontSize: 12, color: "#d97706" }} />
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Custom Activity Groups</span>
                        <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 500 }}>{(selectedItem.custom_groups || []).length} items</span>
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 6 }}>
                        {(selectedItem.custom_groups || []).map((group: any, idx: number) => (
                          <span key={idx} style={{ display: "inline-flex", alignItems: "center", padding: "3px 10px", borderRadius: 9999, fontSize: 11, fontWeight: 500, background: "#fffbeb", color: "#92400e", border: "0.5px solid #fde68a" }}>{group.name || group}</span>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                <div style={{ height: 1, background: "#e2e8f0", margin: "0 28px" }} />

                {/* Audit Trail */}
                <div style={{ padding: "20px 28px 8px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #bbf7d0" }}>
                      <i className="fa-solid fa-clock-rotate-left" style={{ fontSize: 12, color: "#16a34a" }} />
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Audit Trail</span>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                    {[
                      { label: "Created By", content: (<div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}><div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg, #3b82f6, #2563eb)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#fff", flexShrink: 0 }}>{String((selectedItem as any).created_by ?? "?")[0].toUpperCase()}</div><span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{resolveUserName((selectedItem as any).created_by)}</span></div>) },
                      { label: "Created On", content: (<div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}><i className="fa-regular fa-calendar" style={{ fontSize: 14, color: "#3b82f6" }} /><span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{(selectedItem as any).createdat ? new Date((selectedItem as any).createdat).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</span></div>) },
                      { label: "Last Updated", content: (<div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}><i className="fa-regular fa-clock" style={{ fontSize: 14, color: "#10b981" }} /><span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{(selectedItem as any).updatedat ? new Date((selectedItem as any).updatedat).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</span></div>) },
                    ].map((item, i) => (
                      <div key={i} style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                        <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", letterSpacing: "0.02em" }}>{item.label}</span>
                        {item.content}
                      </div>
                    ))}
                  </div>
            {Array.isArray(selectedItem.updated_by) && selectedItem.updated_by.length > 0 && (
  <div style={{ gridColumn: "1 / -1", background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)", marginTop: 12 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
      <i className="fa-solid fa-pen-to-square" style={{ fontSize: 11, color: "#10b981" }} />
      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.02em" }}>Update History</span>
      <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#f0fdf4", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0", marginLeft: "auto" }}>
        {selectedItem.updated_by.length} update{selectedItem.updated_by.length !== 1 ? "s" : ""}
      </span>
    </div>
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {(selectedItem.updated_by as { id?: number; at?: string }[]).slice().reverse().slice(0, showAllUpdates ? undefined : 4).map((entry, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 8, background: i === 0 ? "#f0fdf4" : "#f8fafc", border: `1px solid ${i === 0 ? "#bbf7d0" : "#e2e8f0"}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: i === 0 ? "linear-gradient(135deg, #10b981, #059669)" : "linear-gradient(135deg, #94a3b8, #64748b)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
              {String(resolveUserName(entry.id) ?? "?")[0].toUpperCase()}
            </div>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{resolveUserName(entry.id)}</span>
            {i === 0 && <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#dcfce7", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0" }}>Latest</span>}
          </div>
          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>
            {entry.at ? new Date(entry.at).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
          </span>
        </div>
      ))}
      {selectedItem.updated_by.length > 4 && (
        <div style={{ textAlign: "center", padding: "6px 0" }}>
          <button onClick={() => setShowAllUpdates(v => !v)} style={{ fontSize: 11, color: "#3b82f6", fontWeight: 600, background: "none", border: "none", cursor: "pointer" }}>
            {showAllUpdates ? "Show less ↑" : `+${selectedItem.updated_by.length - 4} more updates ↓`}
          </button>
        </div>
      )}
    </div>
  </div>
)}
                </div>
              </div>

              {/* Footer */}
              <div style={{ padding: "14px 28px", borderTop: "1px solid #e2e8f0", background: "#fff", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10, flexShrink: 0 }}>
                <Button onClick={handleCloseDialog} style={{ background: "linear-gradient(135deg, #1e3a8a, #2563eb)", color: "#fff", fontWeight: 600, padding: "8px 28px", borderRadius: 10, boxShadow: "0 2px 8px rgba(37,99,235,0.3)" }}>Close Details</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ─── Add / Edit Dialog ─── */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-6xl h-[90vh] flex flex-col overflow-hidden p-0">
          <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold text-gray-900">
                {editIndex === null ? "Add Relation  Custom Group" : "Edit Activity Custom Group"}
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-500">
                Fill in the details below to {editIndex === null ? "create" : "update"} an Activity Custom Group.
              </DialogDescription>
            </DialogHeader>
          </div>

          {!isEditLoading ? (
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-0">
              <SectionCard num={1} title="Basic information" subtitle="Name, description and status">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label htmlFor="name">Name <span className="text-red-500 ml-0.5">*</span></Label>
                    <Input id="name" value={Name} onChange={(e) => setName(e.target.value)} placeholder="Enter group name (min 3 characters)" className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Input id="description" value={Description} onChange={(e) => setDescription(e.target.value)} placeholder="Enter description" className="mt-1" />
                  </div>
                </div>
                <div>
                  <Label>Status</Label>
                  <div className="flex gap-2 mt-1">
                    <button type="button" onClick={() => setIsActive(true)} className={`h-9 px-4 rounded-md text-sm font-medium border transition-all ${isActive ? "bg-green-50 text-green-800 border-green-300" : "bg-white text-gray-400 border-gray-200 hover:bg-gray-100"}`}>Active</button>
                    <button type="button" onClick={() => setIsActive(false)} className={`h-9 px-4 rounded-md text-sm font-medium border transition-all ${!isActive ? "bg-red-50 text-red-800 border-red-300" : "bg-white text-gray-400 border-gray-200 hover:bg-gray-100"}`}>Inactive</button>
                  </div>
                </div>
              </SectionCard>

              <SectionCard num={2} title="Activity codes" subtitle="At least one code is required across any type">
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { key: "CPT",    label: "CPT",    border: "border-blue-200",   bg: "bg-blue-100",   text: "text-blue-900",   bdColor: "border-blue-500" },
                    { key: "HCPCS",  label: "HCPCS",  border: "border-green-200",  bg: "bg-green-100",  text: "text-green-900",  bdColor: "border-green-500" },
                    { key: "Dental", label: "Dental", border: "border-purple-200", bg: "bg-purple-100", text: "text-purple-900", bdColor: "border-purple-500" },
                    { key: "Drugs",  label: "Drugs",  border: "border-orange-200", bg: "bg-orange-100", text: "text-orange-900", bdColor: "border-orange-500" },
                  ].map(({ key, label, border, bg, text, bdColor }) => (
                    <div key={key} className={`bg-white p-3 rounded-lg border ${border}`}>
                      <Label className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-1 ${bg} ${text} rounded text-xs font-bold border ${bdColor}`}>{label}</span>
                        <span className="text-sm text-gray-600">({activitiesByType[key as keyof typeof activitiesByType].length} codes)</span>
                      </Label>
                      <ListofActivityCode
                        key={`${key.toLowerCase()}-${activitiesByType[key as keyof typeof activitiesByType].length}`}
                        onChange={(selected: Option[]) => { setActivitiesByType(prev => ({ ...prev, [key]: selected })); }}
                        type={key} defaultValue={activitiesByType[key as keyof typeof activitiesByType]} clear={false} isValid={true} />
                    </div>
                  ))}
                </div>
              </SectionCard>

              <SectionCard num={3} title="Groups" subtitle="Optional medical and custom activity groups">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="activitymedicalGroups">Activity Medical Groups</Label>
                    <ListOfActivityMedical onChange={handleactivitymedicalChange} defaultValue={activitymedicalGroups} clear={clearactivitymedicalGroups} />
                  </div>
                  <div>
                    <Label htmlFor="activitycustomGroups">Activity Custom Groups</Label>
                    <ListOfActivityCustom onChange={handleactivitycustomChange} defaultValue={activitycustomGroups} clear={clearactivitycustomGroups} />
                  </div>
                </div>
              </SectionCard>
            </div>
          ) : (
            <div className="flex-1 flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          )}

          <div className="flex-shrink-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
            <Button variant="outline" onClick={() => { setEditIndex(null); setIsDialogOpen(false); clear(); setIsEditLoading(false); }}>Cancel</Button>
            <Button onClick={() => { editIndex === null ? addItem() : updateItem(editIndex); }} className="bg-blue-700 hover:bg-blue-800 text-white" disabled={isEditLoading}>
              {editIndex === null ? "Add Relation " : "Update Relation "}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── View Codes Dialog ─── */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>All Activity Codes</DialogTitle></DialogHeader>
          <div className="space-y-1 max-h-64 overflow-auto my-2">
            {viewCodes.map((code) => <div key={code} className="text-sm">{code}</div>)}
          </div>
          <DialogFooter className="flex justify-between">
            <Button variant="outline" onClick={() => { const csv = viewCodes.join("\r\n"); const blob = new Blob([csv], { type: "text/csv" }); const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = "activitycode.csv"; a.click(); URL.revokeObjectURL(url); }}>Download CSV</Button>
            <Button onClick={() => setIsViewDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── All Activity Codes Dialog ─── */}
      <Dialog open={activityDialogOpen} onOpenChange={setActivityDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-blue-900 flex items-center gap-2">
              <i className="fa-solid fa-list"></i>
              All Activity Codes
              <span className="ml-auto text-sm font-normal text-gray-600">({selectedActivitiesList?.length || 0} codes)</span>
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 overflow-y-auto max-h-[calc(85vh-140px)] pr-2">
            <div className="grid grid-cols-4 gap-3">
              {selectedActivitiesList && selectedActivitiesList.map((item, index) => {
                const [code, type] = item.split('|||');
                const colors = getActivityTypeColor(type);
                return (
                  <div key={index} className={`flex items-center justify-between gap-2 p-3 bg-white rounded-lg border hover:shadow-md transition-all ${colors.border}`}>
                    <span className={`font-bold text-base ${colors.text}`}>{code}</span>
                    <div className={`w-px h-6 ${colors.border}`}></div>
                    <span className={`text-xs font-bold capitalize ${colors.text} opacity-75`}>{type}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex justify-end pt-4 border-t mt-4">
            <Button onClick={() => setActivityDialogOpen(false)} className="bg-blue-600 hover:bg-blue-700">Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── Force / Hard Delete / Status Dialog ─── */}
      {forceDialogOpen && (
        <div style={{ position: "fixed", inset: 0, background: "#00000088", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(4px)" }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: 32, width: 440, boxShadow: "0 20px 50px rgba(0,0,0,0.3)", border: "1px solid #fecaca" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 12 }}>
              <div style={{ width: 60, height: 60, borderRadius: "50%", background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className="fa-solid fa-triangle-exclamation" style={{ fontSize: 28, color: "#dc2626" }}></i>
              </div>

              {/* Hard Delete */}
              {forceAction === "hardDelete" && (
                <>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}>Permanent Delete</h3>
                  <p style={{ fontSize: 13, color: "#64748b", margin: 0, lineHeight: 1.6 }}>
                    This will <strong style={{ color: "#dc2626" }}>permanently delete</strong> the record.<br />This action <strong>cannot be undone</strong>.
                  </p>
                  {forceError && (
                    <div style={{ width: "100%", background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: "12px 16px", textAlign: "left" }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                        <i className="fa-solid fa-circle-info" style={{ color: "#ca8a04", marginTop: 2, flexShrink: 0 }}></i>
                        <p style={{ fontSize: 13, color: "#713f12", margin: 0, lineHeight: 1.6 }}>{forceError}</p>
                      </div>
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
                    <button onClick={() => { setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null); }}
                      style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Cancel</button>
                    <button onClick={() => forceId !== null && permanentDeleteItem(forceId, true)}
                      style={{ flex: 1, background: "#7f1d1d", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
                      <i className="fa-solid fa-bolt" style={{ marginRight: 8 }}></i>Force Delete Anyway
                    </button>
                  </div>
                </>
              )}

              {/* Soft Delete */}
              {forceAction === "softDelete" && (
                <>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}>Record Is In Use</h3>
                  <div style={{ width: "100%", background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: "12px 16px", textAlign: "left" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                      <i className="fa-solid fa-circle-info" style={{ color: "#ca8a04", marginTop: 2, flexShrink: 0 }}></i>
                      <p style={{ fontSize: 13, color: "#713f12", margin: 0, lineHeight: 1.6 }}>{forceError}</p>
                    </div>
                  </div>
                  <p style={{ fontSize: 13, color: "#64748b", margin: 0 }}>
                    Do you want to <strong style={{ color: "#dc2626" }}>force delete</strong> anyway?
                  </p>
                  <div style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
                    <button onClick={() => { setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null); }}
                      style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Cancel</button>
                    <button onClick={() => { if (forceId) forceSoftDelete(forceId); }}
                      style={{ flex: 1, background: "#dc2626", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
                      <i className="fa-solid fa-bolt" style={{ marginRight: 8 }}></i>Force Delete
                    </button>
                  </div>
                </>
              )}

              {/* Status */}
              {forceAction === "status" && (
                <>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}>Status Change Warning</h3>
                  <div style={{ width: "100%", background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: "12px 16px", textAlign: "left" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                      <i className="fa-solid fa-circle-info" style={{ color: "#ca8a04", marginTop: 2, flexShrink: 0 }}></i>
                      <p style={{ fontSize: 13, color: "#713f12", margin: 0, lineHeight: 1.6 }}>{forceError}</p>
                    </div>
                  </div>
                  <p style={{ fontSize: 13, color: "#64748b", margin: 0 }}>
                    Do you want to <strong style={{ color: "#dc2626" }}>force update status</strong> anyway?
                  </p>
                  <div style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
                    <button onClick={() => { setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null); }}
                      style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Cancel</button>
                    <button
                      onClick={() => {
                        if (!forceId) return;
                        const item = items.result.find(i => i.id === forceId);
                        if (item) toggleActiveStatus(item, true);
                      }}
                      style={{ flex: 1, background: "#dc2626", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
                      <i className="fa-solid fa-bolt" style={{ marginRight: 8 }}></i>Force Update
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <Toaster />
    </div>
  );
};

export default ActivityCustomGroup;
